import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { EmailComposeSendPayload } from '@elva/ui';
import { keys } from '@/api/queryKeys';
import { enrichSentAttachments } from '../adapters/enrichSentAttachments';
import { readEmailProviderThreadId } from '../adapters/emailAdapters';
import { uploadEmailAttachmentHandles } from '../api/emailAttachments';
import { emailApi } from '../api/emailApi';
import { markEmailSyncUnavailable, shouldDisableEmailSync } from '../api/emailSync';

export function useEmailComposeSend() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: EmailComposeSendPayload) => {
      const to = payload.to.trim();
      const body = payload.body.trim();
      const subject = payload.subject.trim();
      const attachment_handles = await uploadEmailAttachmentHandles(payload.attachments);

      const detail = await emailApi.sendLegacyCompose({
        to,
        subject,
        body,
        ...(attachment_handles.length ? { attachment_handles } : {}),
        ...(payload.patientId != null ? { patient_id: payload.patientId } : {}),
      });

      const messageId = detail.id?.trim() ?? '';
      if (!messageId) {
        throw new Error('Compose succeeded but no thread id was returned.');
      }

      const enriched = enrichSentAttachments(
        detail,
        attachment_handles,
        payload.attachments ?? [],
      );

      return {
        messageId,
        syncThreadId: readEmailProviderThreadId(enriched),
        handles: attachment_handles,
        files: payload.attachments ?? [],
        detail: enriched,
      };
    },
    onSuccess: ({ messageId, handles, files, detail }) => {
      queryClient.setQueryData(keys.email.message(messageId), detail);
      void queryClient.invalidateQueries({ queryKey: keys.email.messages() });
      void emailApi.syncThread(messageId).then(async () => {
        try {
          const fresh = await emailApi.getLegacyThread(messageId);
          const enriched = enrichSentAttachments(fresh, handles, files);
          queryClient.setQueryData(keys.email.message(messageId), enriched);
        } catch {
          // keep compose cache
        }
      }).catch((err) => {
        if (shouldDisableEmailSync(err)) {
          markEmailSyncUnavailable();
        }
      });
    },
  });
}
